import { useEffect } from "react";

/**
 * Admin live-preview bridge.
 *
 * The admin split-screen editors embed the real public site in an <iframe>.
 * When the admin focuses a field group, the editor asks the embedded site to
 * scroll to the matching section. Communication is via postMessage so it works
 * even if the site is served from a different origin (configurable through
 * VITE_PUBLIC_SITE_URL); same-origin is the default.
 *
 * Site pages mark scrollable sections with `data-preview-section="<key>"`.
 */

/** Messages the admin editor sends INTO the embedded site. */
const ADMIN_TO_SITE = "ingatlan-admin-preview";
/** Messages the embedded site sends BACK to the admin editor. */
const SITE_TO_ADMIN = "ingatlan-preview-site";

type AdminToSiteMessage = {
  source: typeof ADMIN_TO_SITE;
  type: "scroll-to-section";
  section: string;
};

type SiteToAdminMessage = {
  source: typeof SITE_TO_ADMIN;
  type: "ready";
};

/** Base URL of the public site shown in the preview iframe. */
export function previewBaseUrl(): string {
  const env = import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined;
  const base =
    env && env.trim()
      ? env.trim()
      : typeof window !== "undefined"
        ? window.location.origin
        : "";
  return base.replace(/\/+$/, "");
}

/** Origin used as postMessage targetOrigin (falls back to current origin). */
export function previewOrigin(): string {
  try {
    return new URL(previewBaseUrl()).origin;
  } catch {
    return typeof window !== "undefined" ? window.location.origin : "*";
  }
}

/** Build a cache-busted preview URL for a given path + reload token. */
export function previewUrl(path: string, token: number): string {
  const safePath = path.startsWith("/") ? path : `/${path}`;
  const sep = safePath.includes("?") ? "&" : "?";
  return `${previewBaseUrl()}${safePath}${sep}preview=1&t=${token}`;
}

/** True when the current document is rendered inside the preview iframe. */
export function isInPreviewFrame(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.self === window.top) return false;
  } catch {
    // Cross-origin access throws → we are framed.
    return true;
  }
  return new URLSearchParams(window.location.search).has("preview");
}

/** Admin side: ask the embedded site (iframe window) to scroll to a section. */
export function postScrollToSection(
  target: Window | null | undefined,
  section: string,
): void {
  if (!target) return;
  const msg: AdminToSiteMessage = {
    source: ADMIN_TO_SITE,
    type: "scroll-to-section",
    section,
  };
  try {
    target.postMessage(msg, previewOrigin());
  } catch {
    /* iframe not ready / cross-origin race — ignored, retried on next focus */
  }
}

function scrollToSection(section: string, attempt = 0): void {
  const el = document.querySelector<HTMLElement>(
    `[data-preview-section="${CSS.escape(section)}"]`,
  );
  if (!el) {
    // Content may still be rendering (async data) — retry briefly.
    if (attempt < 12) {
      window.setTimeout(() => scrollToSection(section, attempt + 1), 150);
    }
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.classList.remove("preview-highlight");
  // Force reflow so the animation restarts even on repeated focus.
  void el.offsetWidth;
  el.classList.add("preview-highlight");
  window.setTimeout(() => el.classList.remove("preview-highlight"), 1500);
}

/**
 * Site side: when running inside the preview iframe, listen for scroll
 * requests from the admin editor and announce readiness so the editor can
 * (re)send the current section after each (re)load. No-op when not framed.
 */
export function useSitePreviewBridge(): void {
  useEffect(() => {
    if (!isInPreviewFrame()) return;

    function onMessage(event: MessageEvent) {
      const data = event.data as AdminToSiteMessage | undefined;
      if (!data || data.source !== ADMIN_TO_SITE) return;
      if (data.type === "scroll-to-section" && typeof data.section === "string") {
        scrollToSection(data.section);
      }
    }

    window.addEventListener("message", onMessage);

    // Tell the parent editor we're mounted and ready to receive scroll asks.
    const ready: SiteToAdminMessage = { source: SITE_TO_ADMIN, type: "ready" };
    try {
      window.parent?.postMessage(ready, "*");
    } catch {
      /* ignore */
    }

    return () => window.removeEventListener("message", onMessage);
  }, []);
}

/** Admin side: is this a "site ready" handshake message from the iframe? */
export function isPreviewReadyMessage(data: unknown): boolean {
  return (
    !!data &&
    typeof data === "object" &&
    (data as SiteToAdminMessage).source === SITE_TO_ADMIN &&
    (data as SiteToAdminMessage).type === "ready"
  );
}
